import type { ReactNode } from 'react'
import { useState } from 'react'
import {
  Drawer as BottomDrawer,
  breakpointQueries,
  Button,
  Modal,
  toastManager,
  useMediaQuery,
} from '@systemeio/components'
import { XCloseIcon } from '@systemeio/icons'
import { useTranslation } from 'next-i18next/pages'
import type { KeyedMutator } from 'swr'
import { BadRequest } from 'shared/errors/bad-request'
import { InternalError } from 'shared/errors/internal-error'
import { cancelSubscription } from '../api/cancel-subscriptions-api'
import type {
  ManageSubscriptionsApiInterface,
  ManageSubscriptionsInterface,
} from '../types/manage-subscriptions-interface'

interface CancelSubscriptionModalProps {
  opened: boolean
  onClose: () => void
  subscription: ManageSubscriptionsInterface
  mutate: KeyedMutator<ManageSubscriptionsApiInterface>
}

function CancelSubscriptionModal({
  opened,
  onClose,
  subscription,
  mutate,
}: CancelSubscriptionModalProps): ReactNode {
  const { t } = useTranslation()
  const [error, setError] = useState('')
  const [isFetching, setIsFetching] = useState(false)
  const isMobile = useMediaQuery(breakpointQueries.isMobile, { initializeWithValue: false })

  const handleConfirm = async (): Promise<void> => {
    try {
      setIsFetching(true)
      await cancelSubscription(
        subscription.id,
        subscription.subscriptionItems[0].id,
        subscription.paymentProcessor,
      )
      await mutate(data => {
        if (data) {
          return {
            items: data.items.map(item => {
              if (item.id === subscription.id) item = { ...item, status: 'cancelled' }
              return item
            }),
            hasMore: data.hasMore,
          }
        }
      }, false)
      setIsFetching(false)
      onClose()
      toastManager.add({ type: 'success', title: t('profile.subscriptions.unsubscribed') })
    } catch (error_) {
      setIsFetching(false)
      if (error_ instanceof BadRequest) {
        if (error_.errors.common) {
          setError(error_.errors.common.join(''))
        }
      } else if (error_ instanceof InternalError) {
        setError(t('core.error.internal_error_message'))
      } else {
        toastManager.add({ type: 'error', title: t('core.error.internal_error_message') })
      }
    }
  }

  const title = t('confirmation_modal.default_title')

  const fields = (
    <>
      <p>{t('profile.subscriptions.unsubscribe_confirmation')}</p>
      {error && <p className="text-center text-sm text-text-error">{error}</p>}
    </>
  )

  const renderAction = (buttonClassName?: string): ReactNode => (
    <>
      <Button
        variant="secondary"
        onClick={onClose}
        disabled={isFetching}
        className={buttonClassName}
      >
        {t('confirmation_modal.cancel')}
      </Button>
      <Button
        variant="primary"
        isLoading={isFetching}
        onClick={handleConfirm}
        className={buttonClassName}
      >
        {t('confirmation_modal.confirm')}
      </Button>
    </>
  )

  const action = renderAction()

  return isMobile ? (
    <BottomDrawer.Root
      open={opened}
      onOpenChange={open => {
        if (!open) onClose()
      }}
    >
      <BottomDrawer.Popup>
        <BottomDrawer.Header className="flex-row items-start gap-2.5">
          <BottomDrawer.Title className="min-w-0 flex-1">{title}</BottomDrawer.Title>
          <BottomDrawer.Close
            aria-label="Close"
            className="shrink-0"
            render={<Button variant="ghost-gray" size="icon-sm" />}
          >
            <XCloseIcon />
          </BottomDrawer.Close>
        </BottomDrawer.Header>
        <BottomDrawer.Content className="flex flex-col gap-6">{fields}</BottomDrawer.Content>
        <BottomDrawer.Footer>
          <div className="flex w-full gap-3">{renderAction('min-w-[90px] flex-1')}</div>
        </BottomDrawer.Footer>
      </BottomDrawer.Popup>
    </BottomDrawer.Root>
  ) : (
    <Modal.Root
      open={opened}
      onOpenChange={open => {
        if (!open) onClose()
      }}
    >
      <Modal.Popup>
        <Modal.Header>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>
        <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-8 py-6">{fields}</div>
        <Modal.Footer>{action}</Modal.Footer>
      </Modal.Popup>
    </Modal.Root>
  )
}

export default CancelSubscriptionModal
